import { useSearchParams } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useFraudHubKpis } from '@/api/hooks/useFraudHubKpis';
import { parsePeriod } from '@/lib/timePeriod';

/**
 * Section 1 of the Fraud Hub: five at-a-glance KPI tiles.
 *
 * Visual hierarchy is severity-first via palette + page-order (this is
 * the FIRST section on the page, and Open Critical is the FIRST tile):
 *   1. open_critical_count → red-50 wash + red border + red-900 text
 *   2. open_high_count     → orange-50 wash + orange border
 *   3. total_open_count    → muted neutral
 *   4. new_today_count     → muted neutral ("New in Period" — the backend
 *      computes this over the SELECTED period, not a fixed "today")
 *   5. top_rule            → muted neutral, with rule_name + count (also
 *      period-scoped despite the `_24h` field suffix)
 *
 * The Critical tile uses the same font-size as the other tiles —
 * the soft red palette + leading position do the visual lifting
 * without shouting at the rest of the row.
 *
 * KPIs always have a numeric value (even 0) so there is no "empty
 * state" — only a loading state.
 */
export function HubKpiTiles(): JSX.Element {
  const [searchParams] = useSearchParams();
  // Hub default is 'M' (MTD) — the page-level <TimePeriodSelector
  // defaultPeriod="M" /> seeds the URL on mount so this `parsePeriod` call
  // almost always reads a non-null value.
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubKpis(period);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[104px] w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load KPIs: {error?.message ?? 'unknown error'}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      <KpiTile
        label="Open Critical"
        value={data.open_critical_count}
        tone="critical"
      />
      <KpiTile
        label="Open High"
        value={data.open_high_count}
        tone="high"
      />
      <KpiTile
        label="Total Open"
        value={data.total_open_count}
        tone="neutral"
      />
      <KpiTile
        label="New in Period"
        value={data.new_today_count}
        tone="neutral"
      />
      <TopRuleTile
        ruleId={data.top_rule_id}
        ruleName={data.top_rule_name}
        count={data.top_rule_fire_count_24h}
      />
    </div>
  );
}

type Tone = 'critical' | 'high' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  critical: 'bg-red-50 text-red-900 border-red-200',
  high: 'bg-orange-50 text-orange-900 border-orange-200',
  neutral: '',
};

const TONE_LABEL_CLASS: Record<Tone, string> = {
  critical: 'text-red-900/70',
  high: 'text-orange-900/70',
  neutral: 'text-muted-foreground',
};

interface KpiTileProps {
  label: string;
  value: number;
  tone: Tone;
}

function KpiTile({ label, value, tone }: KpiTileProps): JSX.Element {
  return (
    <Card className={cn('h-[104px]', TONE_CLASS[tone])}>
      <CardContent className="flex h-full flex-col justify-between p-4">
        <div
          className={cn(
            'text-xs font-medium uppercase tracking-wide',
            TONE_LABEL_CLASS[tone],
          )}
        >
          {label}
        </div>
        <div className="text-3xl font-bold tabular-nums leading-none">
          {value.toLocaleString('en-ZA')}
        </div>
      </CardContent>
    </Card>
  );
}

interface TopRuleTileProps {
  ruleId: string | null;
  ruleName: string | null;
  count: number;
}

function TopRuleTile({
  ruleId,
  ruleName,
  count,
}: TopRuleTileProps): JSX.Element {
  const empty = ruleId == null;
  return (
    <Card className="h-[104px]">
      <CardContent className="flex h-full flex-col justify-between p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Top Rule
        </div>
        {empty ? (
          <div>
            <div className="text-3xl font-bold leading-none text-muted-foreground">
              —
            </div>
            <div className="mt-1 truncate text-xs text-muted-foreground">
              (no fires in period)
            </div>
          </div>
        ) : (
          <div>
            <div className="text-2xl font-bold tabular-nums leading-none">
              {count.toLocaleString('en-ZA')}
            </div>
            <div
              className="mt-1 truncate text-xs text-muted-foreground"
              title={ruleName ?? ruleId ?? ''}
            >
              <span className="font-mono">{ruleId}</span>
              {ruleName ? ` · ${ruleName}` : ''}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HubKpiTiles;
